import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Globe2, Briefcase } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/site/FormBits";
import { PageHero } from "@/components/site/PageBits";
import { ChatRoom, MembersList } from "@/components/community/Chat";
import { CourseShowcase } from "@/components/community/CommunityPromo";
import { Spinner } from "@/components/site/ProtectedRoute";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { useSeo, pageSeo } from "@/lib/seo";

const SEO = pageSeo("/community");
const QS = [
  ["works_from", "Where do you work remotely from?", "e.g. Popoyo, Nicaragua · Lisbon · Bali", false],
  ["what_i_do", "What do you do?", "e.g. Web designer, VA, English teacher", false],
  ["how_i_earn", "How do you make money with your skill or trade?", "", true],
  ["offer_community", "What can you offer the community?", "", true],
  ["purpose", "Why are you joining the community?", "", true],
  ["skills", "Top skills (optional)", "", false],
  ["website", "Website or portfolio (optional)", "https://", false],
];

function JoinForm({ member, onDone }) {
  const [f, setF] = useState(() => Object.fromEntries(QS.map(([k]) => [k, member?.[k] || ""])));
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/community/join", f); toast.success("Request sent. Greg will review it soon."); onDone(); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-6 sm:p-8 grid sm:grid-cols-2 gap-5 max-w-4xl" data-testid="community-join-form">
      <div className="sm:col-span-2"><h2 className="text-2xl font-bold text-[#0B2A4A]">Join the Digital Nomad Community</h2><p className="mt-1 text-sm text-slate-600">Free. Every member is approved by Greg to keep the community real and helpful.</p></div>
      {QS.map(([k, label, ph, long]) => (
        <div key={k} className={long ? "sm:col-span-2" : ""}><Field label={label}>{long
          ? <Textarea required rows={3} minLength={2} value={f[k]} placeholder={ph} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`join-${k}`} />
          : <Input required={!label.includes("optional")} minLength={label.includes("optional") ? undefined : 2} value={f[k]} placeholder={ph} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`join-${k}`} />}</Field></div>
      ))}
      <div className="sm:col-span-2"><button disabled={busy} className="rounded-xl bg-[#F08A3C] text-white px-6 py-3 font-semibold hover:bg-[#D96F28] disabled:opacity-60" data-testid="join-submit-btn">{busy ? "Sending..." : "Request to join"}</button></div>
    </form>
  );
}

function Community({ me }) {
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState("general");
  const [members, setMembers] = useState([]);
  useEffect(() => { api.get("/community/channels").then((r) => setChannels(r.data)); api.get("/community/members").then((r) => setMembers(r.data)).catch(() => {}); }, []);
  const isHost = me.role === "host";
  const emails = Object.fromEntries(members.filter((x) => x.email).map((x) => [x.user_id, x.email]));
  const ch = channels.find((c) => c.id === active);
  return (
    <div className="flex flex-col gap-5" data-testid="community-chat">
      <div className="flex gap-2 overflow-x-auto pb-1" role="tablist">
        {channels.map((c) => (
          <button key={c.id} role="tab" aria-selected={c.id === active} onClick={() => setActive(c.id)} className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-[background-color,color,transform] duration-200 hover:-translate-y-0.5 ${c.id === active ? "bg-[#0B2A4A] text-[#F5ECD7] shadow-md" : "bg-white border border-[#0B2A4A]/15 text-[#0B2A4A] hover:border-[#1A9AA8]"}`} data-testid={`channel-tab-${c.id}`}>{c.label}</button>
        ))}
      </div>
      <div className="grid lg:grid-cols-[1fr_300px] gap-5">{ch && <ChatRoom key={ch.id} channel={ch} me={me} emails={emails} />}
        <div className="flex flex-col gap-4"><MembersList rows={members} isHost={isHost} />
          {!isHost && <Link to="/dashboard?tab=messages" className="rounded-full bg-[#0B2A4A] text-[#F5ECD7] px-5 py-3 text-sm font-semibold text-center hover:bg-[#071C33]" data-testid="community-message-greg-btn">Message Greg privately</Link>}
        </div>
      </div>
    </div>
  );
}

function Gate() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [editing, setEditing] = useState(false);
  const load = () => api.get("/community/me").then((r) => setState(r.data)).catch(() => setState({}));
  useEffect(() => { if (user) load(); }, [user]);
  if (user === null) return <Spinner />;
  if (!user) return <div className="bg-white rounded-2xl border border-dashed border-[#0B2A4A]/20 p-8 text-center max-w-2xl mx-auto" data-testid="community-sign-in"><p className="text-slate-700">The community chat is private. Create a free account, tell us about yourself, and Greg will approve you.</p><Link to="/signup?next=/community" className="mt-4 inline-block rounded-xl bg-[#0B2A4A] text-[#F5ECD7] px-5 py-3 font-semibold" data-testid="community-signup-btn">Sign in / Create account</Link></div>;
  if (!state) return <Spinner />;
  const m = state.member;
  if (state.is_host || m?.status === "approved") return <Community me={user} />;
  if (m?.status === "pending" && !editing) return <div className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-8 max-w-2xl" data-testid="community-pending"><Clock className="h-6 w-6 text-[#F08A3C]" /><h2 className="mt-3 text-xl font-bold text-[#0B2A4A]">Your request is waiting for approval</h2><p className="mt-2 text-slate-600">You'll get a notification and an email as soon as Greg approves you.</p><button onClick={() => setEditing(true)} className="mt-4 text-sm font-semibold text-[#147C88]" data-testid="community-edit-request-btn">Edit my answers</button></div>;
  return <>{m?.status === "declined" && <p className="mb-4 text-sm text-rose-700" data-testid="community-declined">Your last request wasn't approved. You can update your answers and try again.</p>}<JoinForm member={m} onDone={() => { setEditing(false); load(); }} /></>;
}

export default function CommunityPage() {
  useSeo(SEO);
  return (
    <article data-testid="community-page">
      <PageHero eyebrow="DIGITAL NOMAD COMMUNITY · WORLDWIDE" title="Digital Nomad Community Chat"
        subtitle="Meet approved digital nomads from around the world: find remote work, offer work, share skills, plan retreats at Esc Digital Nomad Village and help each other thrive.">
        <Link to="/jobs" className="inline-flex items-center gap-2 rounded-xl bg-white/10 border border-white/25 text-white px-6 py-3 font-semibold" data-testid="community-jobs-cta"><Briefcase className="h-4 w-4" /> Browse remote jobs</Link>
        <span className="inline-flex items-center gap-2 text-sm text-[#F5ECD7]/80"><Globe2 className="h-4 w-4 text-[#F08A3C]" /> Free · every member approved</span>
      </PageHero>
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-12"><Gate /></section>
      <CourseShowcase />
    </article>
  );
}
